import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import relativeTime from 'dayjs/plugin/relativeTime'
import 'dayjs/locale/zh-cn'

dayjs.extend(utc)
dayjs.extend(relativeTime)
// 站点通篇中文，相对时间（「3 分钟前」）的语言在这里一次定死，
// 不再依赖「通知页碰巧被加载过」这种隐式前提
dayjs.locale('zh-cn')

/**
 * 接口时间的统一回显入口。凡是展示后端返回的时间，都走这里，不要直接 dayjs(接口值)。
 *
 * 后端返回的是不带时区的墙上时间（LocalDateTime 直接序列化，形如 2026-08-01T17:30:05）。
 * 值里没有任何时区信息，所以「要不要换算」取决于后端跑在哪个时区：
 *   - 本机开发：后端在这台东八区机器上跑，值本身就是北京时间，原样显示即可；
 *   - 线上：容器时区是 UTC，LocalDateTime.now() 落库那一刻就比北京时间慢 8 小时，
 *     需要按 UTC 解释再换算到东八区才是正确的北京时间。
 *
 * 根因与治本方案（容器时区 + 一次性订正历史数据）见
 * logistics-consolidation-admin-backend/tasks-doc/2026/07/20260730-tasks.md，
 * 本轮按「只改回显、不动存储」处理，这里是展示层的补偿。
 *
 * 默认按构建模式判断，无需额外配置；开发构建连生产后端这类情况，
 * 用 .env 里的 VITE_BACKEND_UTC=true / false 显式覆盖。
 */
const CHINA_UTC_OFFSET_MINUTES = 8 * 60

const backendRunsInUtc = () => {
  const override = import.meta.env.VITE_BACKEND_UTC
  if (override !== undefined && override !== '') {
    return String(override) === 'true'
  }
  return import.meta.env.PROD
}

/**
 * 把接口时间解析成北京时间的 dayjs 对象；解析不了返回 null。
 * 需要 .fromNow() 这类 dayjs 能力时用它，只要展示文本用下面的 formatDateTime。
 */
export const toChinaTime = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  // 后端目前吐 ISO 的 T 分隔符，换成空格后两种写法都能解析
  const text = String(value).trim().replace('T', ' ')
  const parsed = backendRunsInUtc()
    ? dayjs.utc(text).utcOffset(CHINA_UTC_OFFSET_MINUTES)
    : dayjs(text)
  return parsed.isValid() ? parsed : null
}

/**
 * 展示接口时间。格式沿用各页面原有的写法，只有解析口径变了。
 * 解析不了时返回 fallback，不让页面上出现 Invalid date。
 */
export const formatDateTime = (value, format = 'YYYY-MM-DD HH:mm:ss', fallback = '-') => {
  const parsed = toChinaTime(value)
  return parsed ? parsed.format(format) : fallback
}

/**
 * 「3 分钟前」这类相对时间。relativeTime 插件在本模块注册，
 * 调用方不必各自 extend（原先只有通知页注册，别的页面调 .fromNow() 会静默失效）。
 */
export const fromNow = (value, fallback = '-') => {
  const parsed = toChinaTime(value)
  return parsed ? parsed.fromNow() : fallback
}
